import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import { useToast } from './Toast';
import { WhatsAppInvoicePreview, checkWhatsAppInvoice, sendWhatsAppInvoice, useIsHeadOffice, useWhatsAppInvoice, whatsAppBlocker } from './WhatsAppInvoicePreview';

/**
 * SendInvoiceModal - Send draft invoices via Zoho email
 *
 * Features:
 * - Preview invoice details
 * - Send via email (Zoho handles the email with template)
 * - Payment terms selection (default 3 days)
 * - Email recipients (customer, CC partner, additional CCs)
 * - "Mark as Sent" option for manual sends
 * - "WhatsApp" (head office): marks a draft sent (no email), then sends the approved
 *   invoice message from the CleanMyBed WhatsApp number
 */
interface SendInvoiceRecord {
  id: string;
  invoice_number?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_company?: string | null;
  total?: number | null;
  due_date?: string | null;
  territory_id?: string | null;
  referral_discount_applied?: string | number | null;
  referral_credit_applied?: string | number | null;
  additional_cc_emails?: string | null;
}

function SendInvoiceModal({ invoice, onClose, onSuccess }: { invoice: SendInvoiceRecord; onClose: () => void; onSuccess?: (msg: string) => void }) {
  const { supabase } = useAuth();
  const { formatCurrency } = useConfig();
  const { showToast } = useToast();

  // Modal mode: 'send', 'markSent' or 'whatsapp'
  const [mode, setMode] = useState('send');
  const isHeadOffice = useIsHeadOffice();
  const whatsApp = useWhatsAppInvoice(mode === 'whatsapp' ? invoice?.id : null);
  const whatsAppBlock = whatsApp.loading ? null : whatsAppBlocker(whatsApp.data, true);

  // Form state
  const [sending, setSending] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('standard');
  const [paymentTerms, setPaymentTerms] = useState('3');
  const [customPaymentTerms, setCustomPaymentTerms] = useState('');
  const [enablePaymentLink, setEnablePaymentLink] = useState(true);
  const [ccPartner, setCcPartner] = useState(true);
  const [recipientEmail, setRecipientEmail] = useState(invoice?.customer_email || '');
  const [additionalCC, setAdditionalCC] = useState(invoice?.additional_cc_emails || '');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [markSentNote, setMarkSentNote] = useState('');

  // Internal note from the source service log, surfaced as a warning here so it
  // is seen before sending. Previously only visible buried in the detail modal.
  const [internalNote, setInternalNote] = useState('');

  // Partner email (from territory)
  const [partnerEmail, setPartnerEmail] = useState('');

  // Bank-proof attachment: only appears when country_config has a path set
  const [bankProofAvailable, setBankProofAvailable] = useState(false);
  const [attachBankProof, setAttachBankProof] = useState(false);

  // Service-certificate attachment. Only appears when 1-10 active certs
  // exist for the invoice's service visit. Cap of 10 protects email
  // deliverability (Resend + most inboxes choke around 25MB total).
  const [certCount, setCertCount] = useState(0);
  const [attachServiceCertificate, setAttachServiceCertificate] = useState(false);

  // Pre-fill additional CC from service log when invoice changes
  useEffect(() => {
    if (invoice?.additional_cc_emails) {
      setAdditionalCC(invoice.additional_cc_emails);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id]);

  // POS invoice detection + service-certificate availability.
  //
  // Looks up the invoice's source service log once. Two things flow from it:
  //   1. If the log has a property_id, default the email template to 'pos'
  //      (Peace of Sleep co-branded variant).
  //   2. Count active (non-revoked) certificates linked to the log via
  //      cert_submissions. Drives whether the "Attach service certificate"
  //      toggle appears.
  useEffect(() => {
    if (!invoice?.id) return;
    let cancelled = false;
    (async () => {
      // A marketing recovery invoice is a recharge to a partner, not a service
      // sold to a customer, so it defaults to the neutral internal email and
      // skips the POS and certificate logic below entirely. Recognised by the
      // marketing charges that were rolled into it.
      const { count: marketingCharges } = await supabase
        .from('marketing_charges')
        .select('id', { count: 'exact', head: true })
        .eq('invoice_id', invoice.id);
      if (cancelled) return;
      if (marketingCharges && marketingCharges > 0) {
        setEmailTemplate('internal');
        return;
      }

      const { data: inv } = await supabase
        .from('invoices')
        .select('service_log_id, customer_id')
        .eq('id', invoice.id)
        .maybeSingle();
      if (cancelled) return;

      // POS template is decided by the customer being a POS customer
      // (customers.is_pos), not by a linked property. A POS customer must
      // default to the POS invoice even when no property is linked.
      if (inv?.customer_id) {
        const { data: cust } = await supabase
          .from('customers')
          .select('is_pos')
          .eq('id', inv.customer_id)
          .maybeSingle();
        if (!cancelled && (cust as { is_pos?: boolean } | null)?.is_pos === true) {
          setEmailTemplate('pos');
        }
      }

      if (!inv?.service_log_id) return;

      const { data: log } = await supabase
        .from('service_logs')
        .select('property_id, internal_notes')
        .eq('id', inv.service_log_id)
        .maybeSingle();
      if (cancelled) return;
      if (log?.property_id) {
        setEmailTemplate('pos');
      }
      if (log?.internal_notes) {
        setInternalNote(log.internal_notes as string);
      }

      // Count active certificates for this service visit.
      const { data: subs } = await supabase
        .from('cert_submissions')
        .select('id')
        .eq('service_log_id', inv.service_log_id);
      if (cancelled || !subs || subs.length === 0) return;
      const submissionIds = subs.map(s => s.id as string);
      const { count } = await supabase
        .from('cert_certificates')
        .select('id', { count: 'exact', head: true })
        .in('submission_id', submissionIds)
        .is('revoked_at', null);
      if (!cancelled) setCertCount(count || 0);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id]);

  // Load partner email on mount
  useEffect(() => {
    if (invoice?.territory_id) {
      loadPartnerEmail();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.territory_id]);

  // Check whether the country has a bank-proof document configured so we know
  // if the optional attach checkbox should be shown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('country_config')
        .select('invoice_bank_proof_path')
        .limit(1)
        .maybeSingle();
      if (!cancelled) {
        setBankProofAvailable(Boolean(data?.invoice_bank_proof_path));
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPartnerEmail = async () => {
    try {
      const { data } = await supabase
        .from('territories')
        .select('email')
        .eq('id', invoice.territory_id)
        .single();

      if (data?.email) {
        setPartnerEmail(data.email);
      }
    } catch (err) {
      console.error('Error loading partner email:', err);
    }
  };

  const handleSend = async () => {
    setSending(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      };

      // Payment link creation is handled server-side by invoice-manage send operation
      // via _shared/payments.ts provider abstraction. No client-side provider calls needed.

      // Parse additional CC emails
      const ccEmails = additionalCC
        .split(',')
        .map((e: string) => e.trim())
        .filter((e: string) => e && e.includes('@'));

      // Build request body
      const body = {
        invoice_id: invoice.id,
        recipient_email: recipientEmail.trim() || undefined,
        email_template: emailTemplate,
        payment_terms: paymentTerms === 'custom' ? customPaymentTerms : paymentTerms,
        enable_payment_link: emailTemplate === 'internal' ? false : enablePaymentLink,
        cc_partner: ccPartner,
        cc_emails: ccEmails,
        notes: invoiceNotes || undefined,
        attach_bank_proof: bankProofAvailable && attachBankProof,
        attach_service_certificate: certCount >= 1 && certCount <= 10 && attachServiceCertificate,
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-manage?operation=send`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify(body)
        }
      );

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send invoice');
      }

      onSuccess?.('Invoice sent successfully!');
      onClose();
    } catch (err) {
      console.error('Error sending invoice:', err);
      showToast('Error sending invoice: ' + (err instanceof Error ? err.message : 'Unknown error'), 'error');
    } finally {
      setSending(false);
    }
  };

  // The one mark-as-sent call, shared by the Mark as Sent and WhatsApp tabs (no email).
  const markSent = async (note: string | undefined) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-manage?operation=mark_sent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ invoice_id: invoice.id, note })
      }
    );

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to mark as sent');
    }
  };

  // WhatsApp: the server checks everything first (check_only: nothing sent or written).
  // Only a clean check marks a draft sent, exactly like Mark as Sent, and then the approved
  // invoice message goes from the CleanMyBed number. If the send itself then fails (Meta
  // refused it), the invoice stays sent and the window stays open to try again.
  const handleWhatsApp = async () => {
    if (!whatsApp.data) return;
    setSending(true);
    const refused = await checkWhatsAppInvoice('invoice', invoice.id);
    if (refused) {
      setSending(false);
      showToast(refused, 'error');
      return;
    }
    try {
      if (whatsApp.data.status === 'draft') {
        await markSent('Sent by WhatsApp');
      }
    } catch (err) {
      showToast('Error: ' + (err instanceof Error ? err.message : 'Unknown error'), 'error');
      setSending(false);
      return;
    }
    const error = await sendWhatsAppInvoice('invoice', invoice.id);
    setSending(false);
    if (error) {
      const wasDraft = whatsApp.data.status === 'draft';
      whatsApp.reload();
      showToast(wasDraft
        ? `The invoice is now marked sent, but the WhatsApp did not go: ${error} Try again from this window.`
        : error, 'error');
      return;
    }
    onSuccess?.(`Invoice sent by WhatsApp to ${whatsApp.data.phone ?? 'the customer'}`);
    onClose();
  };

  const handleMarkAsSent = async () => {
    setSending(true);

    try {
      await markSent(markSentNote || undefined);
      onSuccess?.('Invoice marked as sent');
      onClose();
    } catch (err) {
      console.error('Error marking as sent:', err);
      showToast('Error: ' + (err instanceof Error ? err.message : 'Unknown error'), 'error');
    } finally {
      setSending(false);
    }
  };

  // Fire-and-forget: create reverse bills after invoice is sent
  // Reverse bill auto-creation removed — new method uses split transactions

  // Format date helper
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${d.getFullYear()}`;
  };

  if (!invoice) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '550px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="m-0">Send Invoice</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="modal-body">
          {/* Mode Toggle */}
          <div className="page-tabs mb-lg">
            <button
              type="button"
              className={`page-tab ${mode === 'send' ? 'active' : ''}`}
              onClick={() => setMode('send')}
            >
              Send Email
            </button>
            <button
              type="button"
              className={`page-tab ${mode === 'markSent' ? 'active' : ''}`}
              onClick={() => setMode('markSent')}
            >
              Mark as Sent
            </button>
            {isHeadOffice && (
              <button
                type="button"
                className={`page-tab ${mode === 'whatsapp' ? 'active' : ''}`}
                onClick={() => setMode('whatsapp')}
              >
                WhatsApp
              </button>
            )}
          </div>

          {/* Invoice Preview */}
          <div className="highlight-box highlight-box--primary mb-lg">
            <div className="font-semibold mb-xs">
              {invoice.invoice_number || 'Draft Invoice'}
            </div>
            <div style={{ fontSize: 'var(--font-sm)', color: 'var(--color-text-light)' }}>
              <div>{invoice.customer_name}</div>
              <div>{invoice.customer_email}</div>
              <div className="font-medium" style={{ marginTop: 'var(--space-sm)', color: 'var(--color-text)' }}>
                Total: {formatCurrency(invoice.total || 0)} &middot; Due: {formatDate(invoice.due_date)}
              </div>
            </div>
          </div>

          {/* Internal note warning. Surfaced here so a note left on the service
              log (e.g. "not sure we're sending this to the client") is seen
              before the invoice goes out, not buried in the detail modal. */}
          {internalNote && (
            <div className="highlight-box highlight-box--warning mb-lg">
              <div className="font-semibold mb-xs">⚠️ Internal note on this service</div>
              <div className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{internalNote}</div>
            </div>
          )}

          {mode === 'send' && (
            <>
              {/* Email Template */}
              <div className="form-group">
                <label className="form-label">Email Template</label>
                <select
                  className="form-select"
                  value={emailTemplate}
                  onChange={e => setEmailTemplate(e.target.value)}
                >
                  <option value="standard">Standard</option>
                  <option value="pos">Peace of Sleep (POS Co-branded)</option>
                  <option value="internal">Internal (Partner Recharge)</option>
                </select>
                {emailTemplate === 'internal' && (
                  <div className="form-help">
                    A short, neutral email with the bank details. No payment link, no referral block.
                  </div>
                )}
              </div>

              {/* Optional bank-proof attachment (only when configured) */}
              {bankProofAvailable && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: 'var(--text-base)' }}>
                    <input
                      type="checkbox"
                      checked={attachBankProof}
                      onChange={e => setAttachBankProof(e.target.checked)}
                      className="m-0"
                    />
                    <span>Attach bank account proof</span>
                  </label>
                  <p style={{ margin: '4px 0 0 24px', fontSize: 'var(--text-sm)', color: 'var(--color-text-light)' }}>
                    Adds the country bank account proof PDF to the email. Use when a new customer needs to verify our banking details.
                  </p>
                </div>
              )}

              {/* Optional service-certificate attachment. Hidden when nothing
                  to attach OR when more than 10 certs exist (would balloon
                  the email — admin can issue these from the property page
                  instead in that case). */}
              {certCount >= 1 && certCount <= 10 && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: 'var(--text-base)' }}>
                    <input
                      type="checkbox"
                      checked={attachServiceCertificate}
                      onChange={e => setAttachServiceCertificate(e.target.checked)}
                      className="m-0"
                    />
                    <span>Attach service certificate{certCount > 1 ? `s (${certCount})` : ''}</span>
                  </label>
                  <p style={{ margin: '4px 0 0 24px', fontSize: 'var(--text-sm)', color: 'var(--color-text-light)' }}>
                    Includes the {certCount > 1 ? `${certCount} service certificates` : 'service certificate'} for this visit, signed off by the accredited operator.
                  </p>
                </div>
              )}

              {/* Payment Terms */}
              <div className="form-group">
                <label className="form-label">Payment Terms</label>
                <select
                  className="form-select"
                  value={paymentTerms}
                  onChange={e => setPaymentTerms(e.target.value)}
                >
                  <option value="3">Net 3 Days (Recommended)</option>
                  <option value="0">Due on Receipt</option>
                  <option value="7">Net 7 Days</option>
                  <option value="15">Net 15 Days</option>
                  <option value="30">Net 30 Days</option>
                  <option value="45">Net 45 Days</option>
                  <option value="60">Net 60 Days</option>
                  <option value="custom">Custom...</option>
                </select>
                {paymentTerms === 'custom' && (
                  <input
                    type="text"
                    className="form-input mt-sm"
                    placeholder="Enter custom payment terms"
                    value={customPaymentTerms}
                    onChange={e => setCustomPaymentTerms(e.target.value)}
                  />
                )}
              </div>

              {/* Enable Payment Link. Hidden on the internal recharge email,
                  which is paid by EFT against the bank details only. */}
              {emailTemplate !== 'internal' && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enablePaymentLink}
                    onChange={e => setEnablePaymentLink(e.target.checked)}
                  />
                  <span>Include Payment Link</span>
                </label>
              </div>
              )}

              {/* Email Recipients */}
              <div className="form-group">
                <label className="form-label">Email Recipients</label>

                {/* Customer email (editable) */}
                <input
                  type="email"
                  className="form-input mb-sm"
                  value={recipientEmail}
                  onChange={e => setRecipientEmail(e.target.value)}
                  placeholder="Recipient email address"
                />

                {/* CC Partner */}
                {partnerEmail && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={ccPartner}
                      onChange={e => setCcPartner(e.target.checked)}
                    />
                    <span className="text-sm">CC Partner ({partnerEmail})</span>
                  </label>
                )}

                {/* Additional CC */}
                <input
                  type="text"
                  className="form-input"
                  placeholder="Additional CC emails (comma-separated)"
                  value={additionalCC}
                  onChange={e => setAdditionalCC(e.target.value)}
                />
                <div className="form-help">Separate multiple emails with commas</div>
              </div>

              {/* Invoice Notes */}
              <div className="form-group">
                <label className="form-label">Customer-facing Notes (Optional)</label>
                <div className="form-help" style={{ marginBottom: '0.375rem' }}>These appear on the invoice PDF sent to the customer.</div>
                <textarea
                  className="form-textarea"
                  rows={2}
                  placeholder="e.g. Payment due within 7 days..."
                  value={invoiceNotes}
                  onChange={e => setInvoiceNotes(e.target.value)}
                />
              </div>
            </>
          )}

          {mode === 'whatsapp' && (
            <>
              {whatsApp.loading && <div className="spinner" />}
              {!whatsApp.loading && whatsApp.data?.status === 'draft' && !whatsAppBlock && (
                <div className="alert alert-info mb-lg">
                  This marks the invoice as sent (no email) and sends it by WhatsApp from the CleanMyBed number.
                </div>
              )}
              {!whatsApp.loading && whatsAppBlock && <div className="alert alert-warning mb-lg">{whatsAppBlock}</div>}
              {!whatsApp.loading && whatsApp.data && (
                <WhatsAppInvoicePreview kind="invoice" data={whatsApp.data} formatCurrency={formatCurrency} />
              )}
            </>
          )}

          {mode === 'markSent' && (
            <>
              {/* Mark as Sent Mode */}
              <div className="alert alert-info mb-lg">
                This will mark the invoice as sent without sending an email.
                Use this when you&apos;ve sent the invoice through other channels.
              </div>

              <div className="form-group">
                <label className="form-label">Note (Optional)</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  placeholder="How was this invoice sent? (e.g., printed copy, in-person, etc.)"
                  value={markSentNote}
                  onChange={e => setMarkSentNote(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </button>

          {mode === 'whatsapp' ? (
            <button
              className="btn btn-primary"
              onClick={() => void handleWhatsApp()}
              disabled={sending || whatsApp.loading || !!whatsAppBlock}
            >
              {sending ? 'Sending...' : 'Send by WhatsApp'}
            </button>
          ) : mode === 'send' ? (
            <button
              className="btn btn-primary"
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Send Invoice'}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleMarkAsSent}
              disabled={sending}
            >
              {sending ? 'Saving...' : 'Mark as Sent'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SendInvoiceModal;
